import type { GridComponent } from '@syncfusion/ej2-react-grids'

interface ChartHistoryRow {
  chart_id?: string
}

const isActiveGridRow = (row: Element | undefined): boolean =>
  Boolean(row?.classList.contains('e-active'))

export function getChartHistoryRowId(data: unknown): string {
  if (!data || typeof data !== 'object') {
    return ''
  }
  const chartId = (data as { chart_id?: unknown }).chart_id
  return typeof chartId === 'string' ? chartId.trim() : ''
}

export function shouldSkipForcedChartHistorySelect(
  chartIdToSelect: string,
  userTookOver: boolean,
  lastUserClickedChartId: string,
): boolean {
  if (!chartIdToSelect) {
    return false
  }
  return userTookOver && chartIdToSelect !== lastUserClickedChartId
}

export function shouldApplyForcedChartHistorySelect(
  requestedChartId: string,
  liveSelectedChartId: string,
): boolean {
  const requested = requestedChartId.trim()
  const live = liveSelectedChartId.trim()
  return Boolean(requested && requested === live)
}

export function resolveChartHistoryAppearanceId(
  selectedChartId: string,
  userTookOver: boolean,
  lastUserClickedChartId: string,
): string {
  const selectedId = selectedChartId.trim()
  if (!selectedId) {
    return ''
  }
  if (userTookOver && lastUserClickedChartId !== selectedId) {
    return lastUserClickedChartId
  }
  return selectedId
}

const resolveChartHistoryRowChartId = (
  row: Element,
  index: number,
  records: Array<{ chart_id?: string }>,
): string => {
  const fromDataset = row
    .querySelector<HTMLElement>('[data-chart-history-id]')
    ?.dataset.chartHistoryId?.trim()
  if (fromDataset) {
    return fromDataset
  }
  return records[index]?.chart_id?.trim() ?? ''
}

export function syncChartHistoryEditButtons(
  grid:
    | {
        element?: HTMLElement | null
        getRows?: () => Element[]
        getCurrentViewRecords?: () => Array<{ chart_id?: string }>
      }
    | null
    | undefined,
  editingChartId: string,
): void {
  const editingId = editingChartId.trim()
  const applyState = (btn: HTMLButtonElement, isEditing: boolean): void => {
    btn.disabled = isEditing
    btn.classList.toggle('act-button-edit-disabled', isEditing)
    btn.title = isEditing ? 'Editing is disabled' : 'Edit'
  }
  const rows = grid?.getRows?.() ?? []
  const records = grid?.getCurrentViewRecords?.() ?? []
  if (rows.length > 0) {
    rows.forEach((row, index) => {
      const btn = row.querySelector<HTMLButtonElement>('.act-button-edit')
      if (!btn) {
        return
      }
      const rowId = resolveChartHistoryRowChartId(row, index, records)
      applyState(btn, Boolean(editingId && rowId === editingId))
    })
    return
  }
  const root = grid?.element
  if (!root) {
    return
  }
  root
    .querySelectorAll<HTMLElement>('[data-chart-history-id]')
    .forEach((el) => {
      const btn = el.querySelector<HTMLButtonElement>('.act-button-edit')
      if (!btn) {
        return
      }
      applyState(
        btn,
        Boolean(editingId && (el.dataset.chartHistoryId ?? '') === editingId),
      )
    })
}

export function shouldTakeOverChartHistorySelection(
  clickedChartId: string,
  selectedChartId: string,
  isInteracted: boolean,
): boolean {
  if (!clickedChartId) {
    return false
  }
  return isInteracted || clickedChartId !== selectedChartId
}

export function bindChartHistoryRowSelection(
  row: Element | undefined,
  rowChartId: string | undefined,
  selectedChartId: string,
): void {
  if (!row) {
    return
  }
  const isSelected = Boolean(
    selectedChartId && rowChartId && rowChartId === selectedChartId,
  )
  row.classList.toggle('e-active', isSelected)
  row.classList.toggle('e-selectionbackground', isSelected)
  if (isSelected) {
    row.setAttribute('aria-selected', 'true')
  } else {
    row.removeAttribute('aria-selected')
  }
  row.querySelectorAll('td, .e-rowcell').forEach((cell) => {
    cell.classList.remove('e-active', 'e-selectionbackground')
  })
}

export function clearChartHistorySelectionClasses(
  grid: GridComponent | null | undefined,
): void {
  const root = grid?.element
  if (!root) {
    return
  }
  root.querySelectorAll('.e-row').forEach((row) => {
    row.classList.remove('e-active', 'e-selectionbackground')
    row.removeAttribute('aria-selected')
  })
  root
    .querySelectorAll(
      '.e-rowcell.e-active, td.e-active, .e-selectionbackground',
    )
    .forEach((el) => {
      el.classList.remove('e-active', 'e-selectionbackground')
      el.removeAttribute('aria-selected')
    })
}

export function syncChartHistoryRowAppearance(
  grid: GridComponent | null | undefined,
  selectedChartId: string,
): void {
  if (!grid?.element?.parentNode) {
    return
  }
  const gridRows = grid.getRows?.() ?? []
  const records =
    (grid.getCurrentViewRecords?.() as ChartHistoryRow[] | undefined) ?? []
  gridRows.forEach((row, index) => {
    bindChartHistoryRowSelection(row, records[index]?.chart_id, selectedChartId)
  })
  if (!selectedChartId) {
    clearChartHistorySelectionClasses(grid)
  }
}

export function selectChartHistoryRow(
  grid: GridComponent | null | undefined,
  chartId: string,
  rows: ChartHistoryRow[],
): boolean {
  if (!chartId || !grid?.element?.parentNode) {
    return false
  }
  const viewRecords =
    (grid.getCurrentViewRecords?.() as ChartHistoryRow[] | undefined) ?? []
  let rowIndex = viewRecords.findIndex((row) => row.chart_id === chartId)
  if (rowIndex < 0) {
    const sortColumns = grid.sortSettings?.columns ?? []
    const orderedRows =
      sortColumns.length === 0
        ? rows
        : [...rows].sort((left, right) => {
            for (const column of sortColumns) {
              const field = column.field
              if (!field) {
                continue
              }
              const leftValue = String(
                (left as Record<string, unknown>)[field] ?? '',
              )
              const rightValue = String(
                (right as Record<string, unknown>)[field] ?? '',
              )
              if (leftValue === rightValue) {
                continue
              }
              const comparison = leftValue < rightValue ? -1 : 1
              return String(column.direction).toLowerCase() === 'descending'
                ? -comparison
                : comparison
            }
            return 0
          })
    const fullIndex = orderedRows.findIndex((row) => row.chart_id === chartId)
    if (fullIndex < 0) {
      return false
    }
    const pageSize = grid.pageSettings?.pageSize ?? orderedRows.length
    const currentPage = grid.pageSettings?.currentPage ?? 1
    const targetPage = pageSize > 0 ? Math.floor(fullIndex / pageSize) + 1 : 1
    if (currentPage !== targetPage) {
      grid.goToPage(targetPage)
      return false
    }
    if (viewRecords.length > 0) {
      return false
    }
    rowIndex = pageSize > 0 ? fullIndex % pageSize : fullIndex
  }
  const gridRows = grid.getRows?.() ?? []
  const selectedIndexes =
    (grid.getSelectedRowIndexes?.() as number[] | undefined) ?? []
  if (
    selectedIndexes.length === 1 &&
    selectedIndexes[0] === rowIndex &&
    isActiveGridRow(gridRows[rowIndex])
  ) {
    return true
  }
  grid.selectRow(rowIndex, false)
  syncChartHistoryRowAppearance(grid, chartId)
  const rowsAfterSelect = grid.getRows?.() ?? []
  return isActiveGridRow(rowsAfterSelect[rowIndex])
}
